import axiosInstance from "../network/AxiosInstance";
import { Page } from "../pagination/Page";
import {ViewsCountPerDayAndArtistDTO} from "../models/ViewsCountPerDayAndArtistDTO";
import {ViewsCountPerDayDTO} from "../models/ViewsCountPerDayDTO";
import {ScrapedViewsByPlatformPerDayDTO} from "../models/ScrapedViewsByPlatformPerDayDTO";
import {ScrapedViewsByOfferPerDayDTO} from "../models/ScrapedViewsByOfferPerDayDTO";
import {ScrapedStreamingContextsPerDayAndArtistDTO} from "../models/ScrapedStreamingContextsPerDayAndArtistDTO";
import {ScrapedStreamsPerCountryPerDayAndArtistDTO} from "../models/ScrapedStreamsPerCountryPerDayAndArtistDTO";

export class ScrapedDataService {
    private static buildParams(
        dateFrom: string | null | undefined,
        dateTo: string | null | undefined,
        artist?: string,
        artists?: string[],
        serviceId?: number | undefined,
        page?: number,
        size?: number,
        sort?: string,
    ): URLSearchParams {
        let params = new URLSearchParams();

        if(dateFrom && dateFrom !== 'Invalid Date'){
            params.append("dateFrom", dateFrom);
        }

        if(dateTo && dateTo !== 'Invalid Date'){
            params.append("dateTo", dateTo);
        }

        if (artist) {
            params.append("artist", artist);
        }

        if (serviceId) {
            params.append("serviceId", serviceId.toString());
        }

        if(artists && artists.length > 0){
            artists.forEach(artist => {
                params.append("artists", artist);
            });
        }

        if (page !== undefined) {
            params.append("page", page.toString());
        }

        if (size !== undefined) {
            params.append("size", size.toString());
        }

        if (sort) {
            params.append('sort', sort);
        }

        return params;
    }

    static async getScrapedViewsAndTotalListenersCountPerDayPerArtistPaged(
        id: number | undefined,
        dateFrom: string | null | undefined,
        dateTo: string | null | undefined,
        page: number,
        size: number,
        sort?: string,
        artist?: string,
        serviceId?: number | undefined,
    ): Promise<Page<ViewsCountPerDayAndArtistDTO>> {
        const params = this.buildParams(dateFrom, dateTo, artist, [], serviceId, page, size, sort);

        const request = {
            params: params,
        };

        const response = await axiosInstance.get<Page<ViewsCountPerDayAndArtistDTO>>(
            `/rest/client/client/${id}/scraped-views-and-total-listeners-count-per-day-per-artist-paged`,
            request
        );

        const data = response.data;
        return new Page<ViewsCountPerDayAndArtistDTO>(
            data.content,
            data.totalElements
        );
    }

    static async getAllArtistsFromScrapedData(page: number, size: number, id: number, artist: string | undefined): Promise<Page<string>> {
        const params = new URLSearchParams({
            page: page.toString(),
            size: size.toString()
        })

        if(artist){
            params.append("artist", artist);
        }

        const response = await axiosInstance.get<Page<string>>(`/rest/client/client/${id}/find-all-artists-from-scraped-data`, {params: params})
        const data = response.data;
        return new Page<string>(
            data.content,
            data.totalElements
        );
    }

    static async getScrapedViewsCountPerDayWithArtistFilter(
        id: number | undefined,
        dateFrom: string | null | undefined,
        dateTo: string | null | undefined,
        artist?: string,
        serviceId?: number | undefined,
    ): Promise<ViewsCountPerDayDTO[]> {
        const params = this.buildParams(dateFrom, dateTo, artist, [], serviceId);

        const request = {
            params: params,
        };

        const response = await axiosInstance.get<ViewsCountPerDayDTO[]>(
            `/rest/client/client/${id}/scraped-views-count-per-day-with-artist-filter`,
            request
        );

        const data = response.data;
        return data.map(item => new ViewsCountPerDayDTO(item));
    }

    static async getScrapedViewsCountPerDayAndArtistWithMultipleArtistFilter(
        id: number | undefined,
        dateFrom: string | null | undefined,
        dateTo: string | null | undefined,
        serviceId?: number | undefined,
        artists?: string[]
    ): Promise<ViewsCountPerDayAndArtistDTO[]> {
        const params = this.buildParams(dateFrom, dateTo, '', artists, serviceId);

        const request = {
            params: params,
        };

        const response = await axiosInstance.get<ViewsCountPerDayAndArtistDTO[]>(
            `/rest/client/client/${id}/scraped-views-count-per-day-and-artist-with-multiple-artists-filter`, request
        )

        return response.data;
    }

    static async getScrapedViewsCountPerDayWithMultipleArtistFilter(
        id: number | undefined,
        dateFrom: string | null | undefined,
        dateTo: string | null | undefined,
        artists?: string[],
        serviceId?: number | undefined,
    ): Promise<ViewsCountPerDayDTO[]> {
        const params = this.buildParams(dateFrom, dateTo, '', artists, serviceId);

        const request = {
            params: params,
        };

        const response = await axiosInstance.get<ViewsCountPerDayDTO[]>(
            `/rest/client/client/${id}/scraped-views-count-per-day-with-multiple-artists-filter`,
            request
        );

        const data = response.data;
        return data.map(item => new ViewsCountPerDayDTO(item));
    }

    static async getScrapedViewsAndTotalListenersCountPerArtistPaged(
        id: number | undefined,
        dateFrom: string | null | undefined,
        dateTo: string | null | undefined,
        page: number,
        size: number,
        sort?: string,
        artists?: string[],
        serviceId?: number | undefined,
    ): Promise<Page<ViewsCountPerDayAndArtistDTO>> {
        const params = this.buildParams(dateFrom, dateTo, '', artists, serviceId, page, size, sort);

        const request = {
            params: params,
        };

        const response = await axiosInstance.get<Page<ViewsCountPerDayAndArtistDTO>>(
            `/rest/client/client/${id}/scraped-views-and-total-listeners-count-per-artist-paged`,
            request
        );

        const data = response.data;
        return new Page<ViewsCountPerDayAndArtistDTO>(
            data.content,
            data.totalElements
        );
    }

    static async getScrapedStreamingContextsPerDayPerArtistPaged(
        id: number | undefined,
        dateFrom: string | null | undefined,
        dateTo: string | null | undefined,
        page: number,
        size: number,
        sort?: string,
        artists?: string[],
        serviceId?: number | undefined,
    ): Promise<Page<ScrapedStreamingContextsPerDayAndArtistDTO>> {
        const params = this.buildParams(dateFrom, dateTo, '', artists, serviceId, page, size, sort);

        const request = {
            params: params,
        };

        const response = await axiosInstance.get<Page<ScrapedStreamingContextsPerDayAndArtistDTO>>(
            `/rest/client/client/${id}/scraped-streaming-contexts-per-day-per-artist-paged`,
            request
        );

        const data = response.data;
        return new Page<ScrapedStreamingContextsPerDayAndArtistDTO>(
            data.content,
            data.totalElements
        );
    }

    static async getScrapedStreamsPerCountryPerDayPerArtistPaged(
        id: number | undefined,
        dateFrom: string | null | undefined,
        dateTo: string | null | undefined,
        page: number,
        size: number,
        sort?: string,
        artists?: string[],
        serviceId?: number | undefined,
    ): Promise<Page<ScrapedStreamsPerCountryPerDayAndArtistDTO>> {
        const params = this.buildParams(dateFrom, dateTo, '', artists, serviceId, page, size, sort);

        const request = {
            params: params,
        };

        const response = await axiosInstance.get<Page<ScrapedStreamsPerCountryPerDayAndArtistDTO>>(
            `/rest/client/client/${id}/scraped-streams-per-country-per-day-per-artist-paged`,
            request
        );

        const data = response.data;
        return new Page<ScrapedStreamsPerCountryPerDayAndArtistDTO>(
            data.content,
            data.totalElements
        );
    }

    static async getScrapedViewsByPlatformPerDay(
        id: number | undefined,
        dateFrom: string | null | undefined,
        dateTo: string | null | undefined,
        artist?: string,
        serviceId?: number | undefined,
    ): Promise<ScrapedViewsByPlatformPerDayDTO[]> {
        const params = this.buildParams(dateFrom, dateTo, artist, [], serviceId);

        const request = {
            params: params,
        };

        const response = await axiosInstance.get<ScrapedViewsByPlatformPerDayDTO[]>(
            `/rest/client/client/${id}/scraped-views-count-per-day-by-platform-with-artist-filter`,
            request
        );

        return response.data;
    }

    static async getScrapedViewsByOfferPerDay(
        id: number | undefined,
        dateFrom: string | null | undefined,
        dateTo: string | null | undefined,
        artist?: string,
        serviceId?: number | undefined,
    ): Promise<ScrapedViewsByOfferPerDayDTO[]> {
        const params = this.buildParams(dateFrom, dateTo, artist, [], serviceId);

        const request = {
            params: params,
        };

        const response = await axiosInstance.get<ScrapedViewsByOfferPerDayDTO[]>(
            `/rest/client/client/${id}/scraped-views-count-per-day-by-offer-with-artist-filter`,
            request
        );

        return response.data;
    }
}